import React, { useRef, useState, useEffect } from "react";
import { SwatchesPicker } from "./SwatchesPicker";
import TextAlignmentDropdown from "./TextAlignDropdown";

const TextEditorToolbar = ({
  selectedField,
  updateField,
  selectedFontSize,
  setSelectedFontSize,
  selectedFontFamily,
  setSelectedFontFamily,
}) => {
  const [showFontColorPicker, setShowFontColorPicker] = useState(false);
  const [showFillColorPicker, setShowFillColorPicker] = useState(false);

  const presetColors = [
    "transparent",
    "#000000",
    "#FFFFFF",
    "#FF0000",
    "#FFFF00",
    "#008000",
    "#0000FF",
    "#FF00FF",
    "#800080",
  ];
  const fontColorButtonRef = useRef(null);
  const fillColorButtonRef = useRef(null);
  const containerRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setShowFontColorPicker(false);
        setShowFillColorPicker(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  if (!selectedField || selectedField.type !== "textbox") return null;

  return (
    <div
      className="px-2 py-1 flex space-x-2 items-center bg-white/20 rounded text-xs relative"
      ref={containerRef}
    >
      {/* Font family dropdown */}
      <select
        value={selectedField.fontFamily || selectedFontFamily}
        onChange={(e) => {
          const value = e.target.value;
          updateField(selectedField.id, { fontFamily: value });
          setSelectedFontFamily(value);
        }}
        className="text-color bg-transparent border rounded outline-none p-2"
      >
        <option value="arial">Arial</option>
        <option value="times">Times New Roman</option>
        <option value="courier">Courier New</option>
        <option value="helvetica">Helvetica</option>
        <option value="roboto">Roboto</option>
        <option value="opensans">Open Sans</option>
        <option value="verdana">Verdana</option>
        <option value="comic">Comic Sans Ms</option>
      </select>

      {/* Font Size control */}
      <select
        value={selectedField.size || selectedFontSize}
        onChange={(e) => {
          const size = parseInt(e.target.value, 10);
          updateField(selectedField.id, { size });
          setSelectedFontSize(size);
        }}
        className="text-color bg-transparent border rounded outline-none p-2 w-16"
      >
        <option value="10">10</option>
        <option value="10">11</option>
        <option value="12">12</option>
        <option value="14">14</option>
        <option value="16">16</option>
        <option value="18">18</option>
        <option value="20">20</option>
        <option value="24">24</option>
        <option value="36">36</option>
        <option value="48">48</option>
        <option value="64">64</option>
        <option value="72">72</option>
        <option value="96">96</option>
        <option value="144">144</option>
        <option value="192">192</option>
        <option value="200">200</option>
      </select>

      {/* Bold / Italic / Underline Toggles */}
      <button
        onClick={() =>
          updateField(selectedField.id, { bold: !selectedField.bold })
        }
        className={`p-1 px-3 rounded text-color ${
          selectedField.bold
            ? "bg-tier-secondary font-bold text-tier-primary"
            : ""
        }`}
      >
        <b>B</b>
      </button>
      <button
        onClick={() =>
          updateField(selectedField.id, { italic: !selectedField.italic })
        }
        className={`p-1 px-3 rounded text-color ${
          selectedField.italic
            ? "bg-tier-secondary italic text-tier-primary"
            : ""
        }`}
      >
        I
      </button>
      <button
        onClick={() =>
          updateField(selectedField.id, { underline: !selectedField.underline })
        }
        className={`p-1 px-3 rounded text-color ${
          selectedField.underline
            ? "bg-tier-secondary underline text-tier-primary"
            : ""
        }`}
      >
        U
      </button>

      {/* Font Color Picker */}
      <div className="relative">
        <button
          ref={fontColorButtonRef}
          onClick={() => {
            setShowFontColorPicker((prev) => !prev);
            setShowFillColorPicker(false);
          }}
          className={`p-1 pl-2 border rounded text-color flex flex-row items-center gap-1 transition ${
            showFontColorPicker
              ? "border-tier-secondary bg-white/20"
              : "bg-transparent"
          }`}
        >
          <div className="flex flex-col items-center justify-center">
            <span>A</span>
            <span
              className="w-4 h-1"
              style={{ backgroundColor: selectedField.color }}
            />
          </div>
          <span className="material-symbols-outlined">
            {showFontColorPicker ? "arrow_drop_up" : "arrow_drop_down"}
          </span>
        </button>
        {showFontColorPicker && (
          <div
            className="absolute z-50 mt-2"
            style={{
              left: fontColorButtonRef.current
                ? fontColorButtonRef.current.offsetLeft
                : 0,
            }}
          >
            <SwatchesPicker
              color={selectedField.color || "#000000"}
              presetColors={presetColors}
              onChange={(newColor) =>
                updateField(selectedField.id, { color: newColor })
              }
            />
          </div>
        )}
      </div>

      {/* Fill Color Picker */}
      <div className="relative">
        <button
          ref={fillColorButtonRef}
          onClick={() => {
            setShowFillColorPicker((prev) => !prev);
            setShowFontColorPicker(false);
          }}
          className={`p-1 pl-2 border rounded text-color flex flex-row items-center gap-1 transition ${
            showFillColorPicker
              ? "border-tier-secondary bg-white/20"
              : "bg-transparent"
          }`}
        >
          <div className="flex flex-col items-center justify-center">
            <span className="material-symbols-outlined text-sm">colors</span>
            <span
              className="w-4 h-1 border-0.5 border-alt-color"
              style={{ backgroundColor: selectedField.fillColor }}
            />
          </div>
          <span className="material-symbols-outlined">
            {showFillColorPicker ? "arrow_drop_up" : "arrow_drop_down"}
          </span>
        </button>
        {showFillColorPicker && (
          <div
            className="absolute z-50 mt-2"
            style={{
              left: fillColorButtonRef.current
                ? fillColorButtonRef.current.offsetLeft
                : 0,
            }}
          >
            <SwatchesPicker
              color={selectedField.fillColor || "transparent"}
              presetColors={presetColors}
              onChange={(newColor) =>
                updateField(selectedField.id, { fillColor: newColor })
              }
            />
          </div>
        )}
      </div>

      <TextAlignmentDropdown
        selectedField={selectedField}
        updateField={updateField}
      />
    </div>
  );
};

export default TextEditorToolbar;
